import type { ApiKeyDto } from '@lib/dto/tenant-settings.dto'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface ApiKeyTableProps {
  keys: ApiKeyDto[]
  /** Whether this viewer may revoke this key; revoked keys never show the action. */
  canRevoke: (key: ApiKeyDto) => boolean
  onRevoke?: (key: ApiKeyDto) => void
}

function creatorLabel(key: ApiKeyDto): string {
  if (!key.createdBy) return '—'
  return key.createdBy.kind === 'staff' ? `${key.createdBy.name} (PixelSign)` : key.createdBy.name
}

function lastUsed(key: ApiKeyDto): string {
  return key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : 'Never'
}

/** A tenant's API keys: active first, revoked ones dimmed below. Secrets are never listed. */
export function ApiKeyTable({ keys, canRevoke, onRevoke }: ApiKeyTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Key</TableHead>
          <TableHead>Created by</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Last used</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Status</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {keys.map((key) => (
          <TableRow key={key.id} className={cn(key.revokedAt ? 'text-muted-foreground' : null)}>
            <TableCell className="font-medium">
              <span className="inline-flex items-center gap-2">
                {key.name}
                <Badge tone={key.mode === 'live' ? 'success' : 'warning'}>{key.mode === 'live' ? 'Live' : 'Test'}</Badge>
              </span>
            </TableCell>
            <TableCell className="font-mono text-caption">{key.prefix}…</TableCell>
            <TableCell className="whitespace-nowrap">{creatorLabel(key)}</TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {new Date(key.createdAt).toLocaleDateString()}
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">{lastUsed(key)}</TableCell>
            <TableCell className="text-right">
              {key.revokedAt ? (
                <Badge>Revoked {new Date(key.revokedAt).toLocaleDateString()}</Badge>
              ) : null}
              {!key.revokedAt && canRevoke(key) && onRevoke ? (
                <Button variant="destructive-quiet" size="sm" onClick={() => onRevoke(key)}>
                  Revoke
                </Button>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
